import { SORT_PARAM_NAME } from "@common/hooks/useSorting";
import { styled } from "@mui/material";
import React, { PropsWithChildren } from "react";
import { Link, LinkProps, useSearchParams } from "react-router-dom";

export type LinkWithQueryProps = LinkProps & {
  keepParams?: string[];
  keepSort?: boolean;
  testId?: string;
};

export const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.common.white,
  "&.MuiTypography-root": {
    textDecoration: "none",
  },
}));
const getUrlWithMergedSearchParams = (
  to: string,
  params: URLSearchParams,
  keepParams: string[] = []
) => {
  // Need to have a base domain to construct a valid URL if there's none included in `to`.
  // The value in itself doesn't matter as we won't use it.
  const { pathname, searchParams }: URL = new URL(to, "https://useless.domain");

  const searchParamsEntries = keepParams
    .map((key) => [key, params.get(key)])
    .filter(([_, value]) => value !== null);

  const merged = new URLSearchParams({
    ...Object.fromEntries(searchParamsEntries),
    ...Object.fromEntries(searchParams),
  });
  return `${pathname}?${merged.toString()}`;
};
export const LinkWithQuery = ({
  to,
  keepSort,
  testId,
  keepParams,
  ...props
}: PropsWithChildren<LinkWithQueryProps>) => {
  let [searchParams] = useSearchParams();

  if (!keepSort) {
    searchParams.delete(SORT_PARAM_NAME);
  }
  // handles SF-2243, prevents filter VIP Rooms to translate to a filter on VIP Devices
  searchParams.delete("vip");
  return (
    <StyledLink
      to={getUrlWithMergedSearchParams(to.toString(), searchParams, keepParams)}
      data-testid={testId}
      {...props}
    />
  );
};

const UsableLink = (
  props: PropsWithChildren<LinkWithQueryProps & { keepParams?: string[] }>
) => {
  const { keepParams, keepSort, testId, ...restProps } = props;

  return !!keepParams ? (
    <LinkWithQuery
      {...restProps}
      testId={testId}
      keepSort={keepSort}
      keepParams={keepParams}
    />
  ) : (
    <StyledLink {...restProps} data-testid={testId} />
  );
};

export default UsableLink;
